// Empty state operational pattern (DESIGN_SYSTEM.md §10.12, WORKSPACE_COCKPIT_SPEC.md §10.4, §12).
// Explains what belongs here, why it is empty, and the next valid action.
// Never uses "secure", "safe", "all clear", or "passed". Zero findings explicitly states
// untested scope and evidence limitations.

import { type ReactNode, useId } from 'react';
import type { LucideIcon } from 'lucide-react';
import { CheckCircle, Filter, FolderOpen, Inbox, Info } from 'lucide-react';
import { Button } from './Button.tsx';
import styles from './EmptyState.module.css';

export type EmptyStateVariant =
  'not-started' | 'zero-results' | 'filtered-empty' | 'unavailable' | 'neutral';

export interface EmptyStateAction {
  label: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'tertiary';
}

export interface EmptyStateProps {
  title: string;
  description: string;
  icon?: LucideIcon;
  variant?: EmptyStateVariant;
  untestedScope?: string;
  evidenceLimitations?: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  compact?: boolean;
  children?: ReactNode;
}

const DEFAULT_ICONS: Record<EmptyStateVariant, LucideIcon> = {
  'not-started': FolderOpen,
  'zero-results': CheckCircle,
  'filtered-empty': Filter,
  unavailable: Info,
  // An empty list is not an alert; keep the marker calm (DESIGN_SYSTEM.md §3.3).
  neutral: Inbox,
};

export function EmptyState({
  title,
  description,
  icon,
  variant = 'neutral',
  untestedScope,
  evidenceLimitations,
  action,
  secondaryAction,
  compact = false,
  children,
}: EmptyStateProps) {
  const titleId = useId();
  const Icon = icon ?? DEFAULT_ICONS[variant];

  return (
    <section
      aria-labelledby={titleId}
      data-variant={variant}
      data-compact={compact ? 'true' : undefined}
      className={styles.container}
    >
      <div className={styles.iconWrapper} aria-hidden="true">
        <Icon size={compact ? 20 : 28} />
      </div>

      <div className={styles.content}>
        <h3 id={titleId} className={styles.title}>
          {title}
        </h3>
        <p className={styles.description}>{description}</p>

        {/* Zero-findings contract: Untested scope & limitations box */}
        {(untestedScope || evidenceLimitations) && (
          <div
            className={styles.limitationsBox}
            role="note"
            aria-label="Testing scope and limitations"
          >
            {untestedScope && (
              <p className={styles.limitationItem}>
                <strong>Untested scope:</strong> {untestedScope}
              </p>
            )}
            {evidenceLimitations && (
              <p className={styles.limitationItem}>
                <strong>Evidence limitations:</strong> {evidenceLimitations}
              </p>
            )}
          </div>
        )}

        {/* Action controls */}
        {(action || secondaryAction || children) && (
          <div className={styles.actions}>
            {action && (
              <Button
                variant={action.variant ?? 'primary'}
                size={compact ? 'small' : undefined}
                onPress={action.onPress}
              >
                {action.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant={secondaryAction.variant ?? 'secondary'}
                size={compact ? 'small' : undefined}
                onPress={secondaryAction.onPress}
              >
                {secondaryAction.label}
              </Button>
            )}
            {children}
          </div>
        )}
      </div>
    </section>
  );
}
